import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import {
  getSigningData,
  submitSignature,
  type SigningPageData,
} from "@services/api/signing";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type ScreenState = "loading" | "error" | "signing" | "submitting" | "success";

function computeInitials(name: string | undefined | null): string {
  if (!name) return "";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase())
    .slice(0, 4)
    .join("");
}

export default function SignScreen() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<ScreenState>("loading");
  const [data, setData] = useState<SigningPageData | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [signatureMode, setSignatureMode] = useState<"draw" | "upload">("draw");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Initials are auto-derived from the signer name on load and stay
  // editable. Sent alongside the signature so they get painted on the
  // PDF block, just before the signing date.
  const [initials, setInitials] = useState<string>("");
  const initialsTouchedRef = useRef(false);

  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [pdfWidth, setPdfWidth] = useState<number>(0);
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfError, setPdfError] = useState<boolean>(false);

  useEffect(() => {
    const update = () => {
      const w = pdfContainerRef.current?.clientWidth;
      if (w && w > 0) setPdfWidth(w);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [state]);

  useEffect(() => {
    if (!token) {
      setErrorMsg("Lien invalide.");
      setState("error");
      return;
    }
    getSigningData(token)
      .then((d) => {
        setData(d);
        if (!initialsTouchedRef.current) {
          // Prefer initials pre-set by the conductor; fall back to deriving
          // them from the signer name.
          setInitials(d.initials || computeInitials(d.signer_name));
        }
        if (d.status === "pending") {
          setState("signing");
        } else {
          setErrorMsg(errorMessageForStatus(d.status, d.conductor_name));
          setState("error");
        }
      })
      .catch(() => {
        setErrorMsg("Lien invalide ou expiré.");
        setState("error");
      });
  }, [token]);

  // Canvas drawing logic — scale-aware so touch/mouse coordinates match the canvas bitmap
  useEffect(() => {
    if (state !== "signing") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const getCanvasCoords = (clientX: number, clientY: number) => {
      const r = canvas.getBoundingClientRect();
      // Map CSS pixel position to canvas bitmap coordinates
      const scaleX = canvas.width / r.width;
      const scaleY = canvas.height / r.height;
      return {
        x: (clientX - r.left) * scaleX,
        y: (clientY - r.top) * scaleY,
      };
    };

    const startDraw = (x: number, y: number) => {
      isDrawingRef.current = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const draw = (x: number, y: number) => {
      if (!isDrawingRef.current) return;
      ctx.lineTo(x, y);
      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
      setHasSigned(true);
    };
    const stopDraw = () => { isDrawingRef.current = false; };

    const onMouseDown = (e: MouseEvent) => {
      const { x, y } = getCanvasCoords(e.clientX, e.clientY);
      startDraw(x, y);
    };
    const onMouseMove = (e: MouseEvent) => {
      const { x, y } = getCanvasCoords(e.clientX, e.clientY);
      draw(x, y);
    };
    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      const { x, y } = getCanvasCoords(t.clientX, t.clientY);
      startDraw(x, y);
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      const { x, y } = getCanvasCoords(t.clientX, t.clientY);
      draw(x, y);
    };

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", stopDraw);
    canvas.addEventListener("mouseleave", stopDraw);
    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", stopDraw);

    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseup", stopDraw);
      canvas.removeEventListener("mouseleave", stopDraw);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", stopDraw);
    };
  }, [state, signatureMode]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setErrorMsg("Veuillez sélectionner une image");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMsg("La taille maximale est de 5 Mo.");
      return;
    }

    setUploadedFile(file);
    setHasSigned(true);
  };

  const handleConfirm = async () => {
    if (!token) return;

    let file: File;

    if (signatureMode === "draw") {
      if (!canvasRef.current) return;
      const dataUrl = canvasRef.current.toDataURL("image/png");

      // Convert data URL to Blob
      const blob = await (await fetch(dataUrl)).blob();

      // Convert Blob to File
      file = new File([blob], "signature.png", { type: "image/png" });
    } else {
      if (!uploadedFile) return;
      file = uploadedFile;
    }

    setState("submitting");
    try {
      const result = await submitSignature(token, file, initials);
      if (result.success) {
        setState("success");
      } else {
        setErrorMsg(errorMessageForStatus(result.message, data?.conductor_name || ""));
        setState("error");
      }
    } catch {
      setErrorMsg("Une erreur est survenue. Veuillez réessayer.");
      setState("error");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        fontFamily: "Arial, sans-serif",
        color: "#1e293b",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#1e293b",
          padding: "20px 24px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span style={{ color: "white", fontSize: 18, fontWeight: "bold" }}>
          Signature de document
        </span>
      </div>

      <div
        style={{
          maxWidth: 680,
          margin: "0 auto",
          padding: "24px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* LOADING */}
        {state === "loading" && (
          <div style={{ textAlign: "center", paddingTop: 60, color: "#64748b" }}>
            Chargement…
          </div>
        )}

        {/* ERROR */}
        {state === "error" && (
          <div
            style={{
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              borderRadius: 12,
              padding: 24,
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <p style={{ color: "#be123c", fontSize: 16, margin: 0 }}>{errorMsg}</p>
          </div>
        )}

        {/* SIGNING / SUBMITTING */}
        {(state === "signing" || state === "submitting") && data && (
          <>
            {/* Info card */}
            <div
              style={{
                background: "white",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: "bold" }}>{data.doc_title}</div>
              <div style={{ color: "#64748b", fontSize: 14 }}>
                Projet : {data.project_name}
              </div>
              <div style={{ color: "#64748b", fontSize: 14 }}>
                Demandé par : {data.conductor_name}
              </div>
              <div style={{ color: "#475569", fontSize: 14, marginTop: 4 }}>
                Signataire : <strong>{data.signer_name}</strong>
              </div>
            </div>

            {/* PDF preview — uses pdf.js so it renders on mobile too */}
            {data.pdf_url && (
              <div
                style={{
                  background: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                <div
                  ref={pdfContainerRef}
                  style={{
                    width: "100%",
                    maxHeight: "70vh",
                    overflowY: "auto",
                    background: "#f1f5f9",
                  }}
                >
                  {pdfError ? (
                    <div style={{ padding: 24, textAlign: "center" }}>
                      <p style={{ margin: "0 0 12px 0", color: "#64748b", fontSize: 14 }}>
                        Impossible d'afficher le document ici.
                      </p>
                      <a
                        href={data.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-block",
                          padding: "10px 20px",
                          background: "#1e293b",
                          color: "white",
                          textDecoration: "none",
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        Ouvrir le PDF
                      </a>
                    </div>
                  ) : (
                    <Document
                      file={data.pdf_url}
                      onLoadSuccess={({ numPages: n }) => {
                        setNumPages(n);
                        setPdfError(false);
                      }}
                      onLoadError={() => setPdfError(true)}
                      loading={
                        <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 14 }}>
                          Chargement du document…
                        </div>
                      }
                    >
                      {pdfWidth > 0 &&
                        Array.from({ length: numPages }, (_, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              padding: "8px 0",
                              borderBottom:
                                i < numPages - 1 ? "1px solid #e2e8f0" : "none",
                            }}
                          >
                            <Page
                              pageNumber={i + 1}
                              width={pdfWidth}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          </div>
                        ))}
                    </Document>
                  )}
                </div>
                {!pdfError && numPages > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 14px",
                      borderTop: "1px solid #e2e8f0",
                      fontSize: 12,
                      color: "#64748b",
                      background: "white",
                    }}
                  >
                    <span>{numPages} page{numPages > 1 ? "s" : ""}</span>
                    <a
                      href={data.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#1e293b",
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      Ouvrir dans un nouvel onglet
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Signature canvas/upload */}
            <div
              style={{
                background: "white",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {/* Initials — auto-filled from the signer name, editable. */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label
                  htmlFor="signer-initials"
                  style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}
                >
                  Mes initiales
                </label>
                <input
                  id="signer-initials"
                  type="text"
                  value={initials}
                  onChange={(e) => {
                    initialsTouchedRef.current = true;
                    // Allow letters and a few latin extended; keep it short.
                    const cleaned = e.target.value.toUpperCase().replace(/[^A-ZÀ-ÖØ-Þ.\-\s]/g, "").slice(0, 6);
                    setInitials(cleaned);
                  }}
                  maxLength={6}
                  style={{
                    width: 130,
                    padding: "8px 10px",
                    border: "1px solid #cbd5e1",
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    letterSpacing: 1,
                    color: "#1e293b",
                    background: "#f8fafc",
                  }}
                  placeholder="JD"
                  disabled={state === "submitting"}
                  autoComplete="off"
                />
                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                  Apparaîtront sur le document signé, juste avant la date.
                </div>
              </div>

              {/* Tabs */}
              <div style={{ display: "flex", gap: 10, borderBottom: "2px solid #e2e8f0" }}>
                <button
                  onClick={() => {
                    setSignatureMode("draw");
                    setUploadedFile(null);
                    setHasSigned(false);
                  }}
                  disabled={state === "submitting"}
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    background: "transparent",
                    borderBottom: signatureMode === "draw" ? "3px solid #1e293b" : "none",
                    color: signatureMode === "draw" ? "#1e293b" : "#64748b",
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: signatureMode === "draw" ? "bold" : "normal",
                    marginBottom: "-2px",
                  }}
                >
                  Dessiner
                </button>
                <button
                  onClick={() => {
                    setSignatureMode("upload");
                    clearCanvas();
                    setHasSigned(false);
                  }}
                  disabled={state === "submitting"}
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    background: "transparent",
                    borderBottom: signatureMode === "upload" ? "3px solid #1e293b" : "none",
                    color: signatureMode === "upload" ? "#1e293b" : "#64748b",
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: signatureMode === "upload" ? "bold" : "normal",
                    marginBottom: "-2px",
                  }}
                >
                  Télécharger
                </button>
              </div>

              {/* Draw mode */}
              {signatureMode === "draw" && (
                <>
                  <div style={{ fontSize: 14, color: "#64748b" }}>
                    Signez dans le cadre ci-dessous (doigt ou souris)
                  </div>
                  <canvas
                    ref={canvasRef}
                    width={620}
                    height={180}
                    style={{
                      border: "2px dashed #cbd5e1",
                      borderRadius: 8,
                      background: "#f8fafc",
                      width: "100%",
                      touchAction: "none",
                      cursor: state === "submitting" ? "not-allowed" : "crosshair",
                    }}
                  />
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button
                      onClick={clearCanvas}
                      disabled={state === "submitting"}
                      style={{
                        padding: "8px 20px",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        background: "white",
                        color: "#64748b",
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                    >
                      Effacer
                    </button>
                    <button
                      onClick={handleConfirm}
                      disabled={!hasSigned || state === "submitting"}
                      style={{
                        padding: "10px 28px",
                        borderRadius: 8,
                        border: "none",
                        background: hasSigned && state !== "submitting" ? "#1e293b" : "#94a3b8",
                        color: "white",
                        cursor: hasSigned && state !== "submitting" ? "pointer" : "not-allowed",
                        fontSize: 14,
                        fontWeight: "bold",
                      }}
                    >
                      {state === "submitting" ? "Envoi…" : "Confirmer la signature"}
                    </button>
                  </div>
                </>
              )}

              {/* Upload mode */}
              {signatureMode === "upload" && (
                <>
                  <div style={{ fontSize: 14, color: "#64748b" }}>
                    Téléchargez votre signature (image)
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                  />
                  <div
                    onClick={() => !state || state === "signing" ? fileInputRef.current?.click() : null}
                    style={{
                      border: "2px dashed #cbd5e1",
                      borderRadius: 8,
                      background: "#f8fafc",
                      padding: 40,
                      textAlign: "center",
                      cursor: state === "submitting" ? "not-allowed" : "pointer",
                      minHeight: 180,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 12,
                    }}
                  >
                    {uploadedFile ? (
                      <>
                        <div style={{ fontSize: 48 }}>✓</div>
                        <div style={{ fontSize: 14, color: "#1e293b", fontWeight: 500 }}>
                          {uploadedFile.name}
                        </div>
                        <img
                          src={URL.createObjectURL(uploadedFile)}
                          alt="Signature preview"
                          style={{ maxHeight: 100, maxWidth: "100%", objectFit: "contain" }}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setUploadedFile(null);
                            setHasSigned(false);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 6,
                            border: "1px solid #e2e8f0",
                            background: "white",
                            color: "#64748b",
                            cursor: "pointer",
                            fontSize: 13,
                          }}
                        >
                          Changer
                        </button>
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#94a3b8"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <div style={{ fontSize: 14, color: "#64748b" }}>
                          Cliquez pour télécharger une image
                        </div>
                        <div style={{ fontSize: 12, color: "#94a3b8" }}>
                          PNG, JPG ou WEBP (max. 5 Mo)
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button
                      onClick={handleConfirm}
                      disabled={!uploadedFile || state === "submitting"}
                      style={{
                        padding: "10px 28px",
                        borderRadius: 8,
                        border: "none",
                        background: uploadedFile && state !== "submitting" ? "#1e293b" : "#94a3b8",
                        color: "white",
                        cursor: uploadedFile && state !== "submitting" ? "pointer" : "not-allowed",
                        fontSize: 14,
                        fontWeight: "bold",
                      }}
                    >
                      {state === "submitting" ? "Envoi…" : "Confirmer la signature"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* SUCCESS */}
        {state === "success" && data && (
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 12,
              padding: 32,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: 48 }}>✓</div>
            <div style={{ fontSize: 18, fontWeight: "bold", color: "#15803d" }}>
              Signature enregistrée
            </div>
            <p style={{ color: "#166534", margin: 0 }}>
              Merci {data.signer_name}, votre signature a bien été enregistrée.
            </p>
            <p style={{ color: "#64748b", fontSize: 13, margin: 0 }}>
              {data.doc_title}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function errorMessageForStatus(status: string, conductorName: string): string {
  switch (status) {
    case "expired":
      return conductorName
        ? `Ce lien a expiré. Contactez ${conductorName} pour recevoir un nouveau lien.`
        : "Ce lien a expiré.";
    case "signed":
    case "already_signed":
      return "Vous avez déjà signé ce document. Merci !";
    case "superseded":
      return "Ce lien n'est plus valide. Un nouveau lien vous a peut-être été envoyé.";
    default:
      return "Lien invalide ou introuvable.";
  }
}
